import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImagePlus, MapPin, PoundSterling, Clock, FileText } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateListingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', category: 'sale', price: '',
    location: user?.location || '', lending_duration: '', lending_deposit: '', lending_terms: ''
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target?.value ?? e });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images');
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setImages(prev => [...prev, { path: data.path, preview: URL.createObjectURL(file) }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    if (form.category === 'sale' && !form.price) {
      toast.error('Price is required for sale items');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        price: form.category === 'sale' ? parseFloat(form.price) : null,
        location: form.location,
        images: images.map(img => img.path),
        lending_duration: form.category === 'lending' ? form.lending_duration : null,
        lending_deposit: form.category === 'lending' && form.lending_deposit ? parseFloat(form.lending_deposit) : null,
        lending_terms: form.category === 'lending' ? form.lending_terms : null,
      };
      const { data } = await api.post('/listings', payload);
      toast.success('Listing created!');
      navigate(`/listings/${data.listing_id}`);
    } catch {
      toast.error('Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="create-listing-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#2C362F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Post a Listing
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Images */}
          <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
            <Label className="text-sm text-foreground/80 font-medium mb-3 block">Photos (up to 5)</Label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-muted">
                  <img src={img.preview || `${API_BASE}/api/files/${img.path}`} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {images.length < 5 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-[#E8E6DF] flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid="image-upload-area">
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : <ImagePlus className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />}
                  <span className="text-xs text-muted-foreground mt-1">Add Photo</span>
                </label>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 space-y-4">
            <div>
              <Label className="text-sm text-foreground/80 font-medium">Title</Label>
              <Input value={form.title} onChange={update('title')} placeholder="What are you listing?" className="mt-1.5 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" required data-testid="listing-title-input" />
            </div>
            <div>
              <Label className="text-sm text-foreground/80 font-medium">Description</Label>
              <Textarea value={form.description} onChange={update('description')} placeholder="Describe your item, its condition, and any details..." className="mt-1.5 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] min-h-[120px]" required data-testid="listing-description-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-foreground/80 font-medium">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" data-testid="listing-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">For Sale</SelectItem>
                    <SelectItem value="free">Free / Giveaway</SelectItem>
                    <SelectItem value="lending">Lending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-foreground/80 font-medium">Location</Label>
                <div className="relative mt-1.5">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input value={form.location} onChange={update('location')} placeholder="City or postcode" className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" data-testid="listing-location-input" />
                </div>
              </div>
            </div>

            {form.category === 'sale' && (
              <div>
                <Label className="text-sm text-foreground/80 font-medium">Price (£)</Label>
                <div className="relative mt-1.5">
                  <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input type="number" min="0" step="0.01" value={form.price} onChange={update('price')} placeholder="0.00" className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" required data-testid="listing-price-input" />
                </div>
              </div>
            )}

            {form.category === 'lending' && (
              <div className="space-y-4 pt-2 border-t border-[#E8E6DF]">
                <h3 className="text-sm font-semibold text-[#2C362F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Lending Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-foreground/80 font-medium">Duration</Label>
                    <div className="relative mt-1.5">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input value={form.lending_duration} onChange={update('lending_duration')} placeholder="e.g., 1 week" className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" data-testid="lending-duration-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-foreground/80 font-medium">Deposit ($)</Label>
                    <div className="relative mt-1.5">
                      <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input type="number" min="0" step="0.01" value={form.lending_deposit} onChange={update('lending_deposit')} placeholder="Optional" className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11" data-testid="lending-deposit-input" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-foreground/80 font-medium">Terms & Conditions</Label>
                  <Textarea value={form.lending_terms} onChange={update('lending_terms')} placeholder="Specify any lending terms or agreements..." className="mt-1.5 rounded-xl bg-[#FDFBF7] border-[#E8E6DF]" data-testid="lending-terms-input" />
                </div>
              </div>
            )}
          </div>

          <Button type="submit" disabled={submitting} className="w-full rounded-xl h-12 bg-primary hover:bg-primary/90 text-white text-base" data-testid="create-listing-submit-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 mr-2" strokeWidth={1.5} />}
            Publish Listing
          </Button>
        </form>
      </div>
    </div>
  );
}
